import {useContext} from "react";
import {ApiContext} from "../../util/Requests";
import {SC_Permissions} from "../../util/api/SC_Permissions";


const PermissionsRequired = ({
    children,
    invalidChildren = null,
    invert = false,
    readWorkorderImages = false,
    writeWorkorderImages = false,
    readReports = false,
    writeReports = false,
    readLocations = false,
    writeLocations = false,
    writeCrews = false,
    readNotes = false,
    readInternalNotes = false,
    writeInternalNotes = false,
    readExternalNotes = false,
    writeExternalNotes = false,
    readBudget = false,
    writeBudget = false,
    readAdvancedPayroll = false,
    writeAdvancedPayroll = false,
    readReminders = false,
    writeReminders = false,
    readBudgetMoney = false,
    writeBudgetMoney = false,
    scheduleService = false,
    editService = false,
    atLeastCoOwnData = false,
}: {
    children: React.ReactNode;
    invalidChildren?: React.ReactNode | null;
    invert?: boolean;
    readWorkorderImages?: boolean;
    writeWorkorderImages?: boolean;
    readReports?: boolean;
    writeReports?: boolean;
    readLocations?: boolean;
    writeLocations?: boolean;
    writeCrews?: boolean;
    readNotes?: boolean;
    readInternalNotes?: boolean;
    writeInternalNotes?: boolean;
    readExternalNotes?: boolean;
    writeExternalNotes?: boolean;
    readBudget?: boolean;
    writeBudget?: boolean;
    readAdvancedPayroll?: boolean;
    writeAdvancedPayroll?: boolean;
    readReminders?: boolean;
    writeReminders?: boolean;
    readBudgetMoney?: boolean;
    writeBudgetMoney?: boolean;
    scheduleService?: boolean;
    editService?: boolean;
    atLeastCoOwnData?: boolean;
}) => {
    const { caches } = useContext(ApiContext);

    const validReturn = invert ? invalidChildren : children;
    const invalidReturn = invert ? children : invalidChildren;

    const perms: SC_Permissions | false = caches.permissions.value;

    if (!perms)
        return invalidChildren;

    const canReadWorkorderImages = perms.readWorkorderImages;
    const canWriteWorkorderImages = perms.writeWorkorderImages;

    if (readWorkorderImages && !canReadWorkorderImages) {
        return invalidReturn;
    }

    if (writeWorkorderImages && !canWriteWorkorderImages) {
        return invalidReturn;
    }

    const canReadAdvancedPayroll = perms.viewAdvancedPayroll;
    const canWriteAdvancedPayroll = perms.writeAdvancedPayroll;

    if (readAdvancedPayroll && !canReadAdvancedPayroll) {
        return invalidReturn;
    }

    if (writeAdvancedPayroll && !canWriteAdvancedPayroll) {
        return invalidReturn;
    }

    const canReadReports = perms.viewReports;
    const canWriteReports = perms.writeReports;

    if (readReports && !canReadReports) {
        return invalidReturn;
    }

    if (writeReports && !canWriteReports) {
        return invalidReturn;
    }

    const canReadLocations = perms.viewLocations;
    const canWriteLocations = perms.writeLocations;

    if (readLocations && !canReadLocations) {
        return invalidReturn;
    }

    if (writeLocations && !canWriteLocations) {
        return invalidReturn;
    }

    const canWriteCrews = perms.writeCrews;

    if (writeCrews && !canWriteCrews) {
        return invalidReturn;
    }

    const canReadBudget = perms.viewBudget;
    const canWriteBudget = perms.writeBudget;

    if (readBudget && !canReadBudget) {
        return invalidReturn;
    }

    if (writeBudget && !canWriteBudget) {
        return invalidReturn;
    }

    const canReadReminders = perms.viewReminders;
    const canWriteReminders = perms.writeReminders;

    if (readReminders && !canReadReminders) {
        return invalidReturn;
    }

    if (writeReminders && !canWriteReminders) {
        return invalidReturn;
    }

    const canReadBudgetMoney = perms.viewBudgetMonies;
    const canWriteBudgetMoney = perms.writeBudgetMonies;

    if (readBudgetMoney && !canReadBudgetMoney) {
        return invalidReturn;
    }

    if (writeBudgetMoney && !canWriteBudgetMoney) {
        return invalidReturn;
    }

    const canReadInternalNotes = perms.readInternalNotes;
    const canWriteInternalNotes = perms.writeInternalNotes;

    const canReadExternalNotes = perms.readExternalNotes;
    const canWriteExternalNotes = perms.writeExternalNotes;

    if (readNotes && !(canReadInternalNotes || canReadExternalNotes)) {
        return invalidReturn;
    }

    if (readInternalNotes && !canReadInternalNotes) {
        return invalidReturn;
    }

    if (writeInternalNotes && !canWriteInternalNotes) {
        return invalidReturn;
    }

    if (readExternalNotes && !canReadExternalNotes) {
        return invalidReturn;
    }

    if (writeExternalNotes && !canWriteExternalNotes) {
        return invalidReturn;
    }

    if (scheduleService && !perms.scheduleService) {
        return invalidReturn;
    }

    if (editService && !perms.editService) {
        return invalidReturn;
    }

    if (atLeastCoOwnData && !perms.atLeastCoOwn) {
        return invalidReturn;
    }

    return validReturn;
}

export default PermissionsRequired;
